import { useEffect, useState, type FormEvent } from "react";

import { Alert, Badge, Button, Card, CardTitle, Input, Label, Tab, TabList } from "@realroof/ui";

import { api, canManage } from "../api";
import { useSession } from "../session";
import { Table, td, th } from "../table";
import { NumberLimits } from "./NumberLimits";

interface Tag {
  id: number;
  module: "property" | "hall" | "service";
  category: "residential" | "commercial" | null; // property tags only
  name: string;
  sort: number;
  active: boolean;
  is_floor_plan: boolean;
}

/** The tag lists: property tags are residential or commercial, never mixed. */
type TagList = "residential" | "commercial" | "hall" | "service";
const LISTS: [TagList, string][] = [
  ["residential", "Residential"],
  ["commercial", "Commercial"],
  ["hall", "Function halls"],
  ["service", "Services"],
];
const inList = (t: Tag, list: TagList) => (list === "residential" || list === "commercial" ? t.module === "property" && t.category === list : t.module === list);
const newTag = (list: TagList) => (list === "residential" || list === "commercial" ? { module: "property", category: list } : { module: list });

/** Settings: how long a visitor's mobile check lasts, the photo tags owners choose from, and number limits. */
export function Settings() {
  const { me } = useSession();
  const manage = canManage(me?.staff_role);
  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Settings</h1>
      <GuestDays manage={manage} />
      <PhotoTags manage={manage} />
      <NumberLimits manage={manage} />
    </section>
  );
}

function GuestDays({ manage }: { manage: boolean }) {
  const [days, setDays] = useState("");
  const [message, setMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);

  useEffect(() => {
    api<{ guest_days: number }>("/admin/settings/contact")
      .then((s) => setDays(String(s.guest_days)))
      .catch((err: Error) => setMessage({ kind: "error", text: err.message }));
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    try {
      await api("/admin/settings/contact", { method: "PUT", body: { guest_days: Number(days) } });
      setMessage({ kind: "success", text: "Saved. New checks last this long; earlier ones keep their date." });
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message });
    }
  }

  return (
    <Card>
      <CardTitle className="text-base">Seeing numbers without an account</CardTitle>
      <p className="m-0 text-sm text-muted-foreground">
        Visitors verify their mobile by OTP and accept the terms; they can then see owners&apos; numbers on that device for this many days. Logged-in
        users with a verified mobile never need an OTP.
      </p>
      <form className="flex flex-wrap items-end gap-3" onSubmit={save}>
        <Label>
          Days a mobile check lasts
          <Input type="number" min={1} max={90} value={days} disabled={!manage} onChange={(e) => setDays(e.target.value)} className="w-28" />
        </Label>
        {manage && <Button type="submit">Save</Button>}
      </form>
      {message && <Alert tone={message.kind}>{message.text}</Alert>}
    </Card>
  );
}

function PhotoTags({ manage }: { manage: boolean }) {
  const [list, setList] = useState<TagList>("residential");
  const [tags, setTags] = useState<Tag[] | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  const load = () =>
    api<Tag[]>("/admin/photo-tags")
      .then(setTags)
      .catch((err: Error) => setError(err.message));
  useEffect(() => {
    load();
  }, []);

  async function run(action: () => Promise<unknown>) {
    setError("");
    try {
      await action();
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const rows = (tags ?? []).filter((t) => inList(t, list)).sort((a, b) => a.sort - b.sort);
  const move = (t: Tag, d: -1 | 1) => {
    const other = rows[rows.indexOf(t) + d];
    if (!other) return;
    run(async () => {
      await api(`/admin/photo-tags/${t.id}`, { method: "PATCH", body: { sort: other.sort } });
      await api(`/admin/photo-tags/${other.id}`, { method: "PATCH", body: { sort: t.sort } });
    });
  };

  return (
    <Card>
      <CardTitle className="text-base">Photo tags</CardTitle>
      <p className="m-0 text-sm text-muted-foreground">
        Owners tag each photo with one of these. Photos tagged as a floor plan show under &ldquo;Floor plans&rdquo; on the ad. Hidden tags stay on
        photos that already have them.
      </p>
      <TabList>
        {LISTS.map(([m, label]) => (
          <Tab key={m} selected={m === list} className="flex-none" onClick={() => setList(m)}>
            {label}
          </Tab>
        ))}
      </TabList>
      {error && <Alert tone="error">{error}</Alert>}
      {tags === null && <p className="text-muted-foreground">Loading…</p>}
      {tags && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Tag</th>
              <th className={th}>Shown</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((t, i) => (
              <tr key={t.id}>
                <td className={td}>
                  {manage ? (
                    <Input
                      aria-label={`Name of ${t.name}`}
                      defaultValue={t.name}
                      onBlur={(e) => e.target.value.trim() !== t.name && run(() => api(`/admin/photo-tags/${t.id}`, { method: "PATCH", body: { name: e.target.value } }))}
                    />
                  ) : (
                    t.name
                  )}
                  {t.is_floor_plan && <Badge className="ml-2">Floor plan</Badge>}
                </td>
                <td className={td}>{t.active ? "Yes" : "Hidden"}</td>
                <td className={`${td} whitespace-nowrap`}>
                  {manage && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" aria-label={`Move ${t.name} up`} disabled={i === 0} onClick={() => move(t, -1)}>
                        ↑
                      </Button>
                      <Button size="sm" variant="ghost" aria-label={`Move ${t.name} down`} disabled={i === rows.length - 1} onClick={() => move(t, 1)}>
                        ↓
                      </Button>
                      <Button size="sm" onClick={() => run(() => api(`/admin/photo-tags/${t.id}`, { method: "PATCH", body: { active: !t.active } }))}>
                        {t.active ? "Hide" : "Show"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => run(() => api(`/admin/photo-tags/${t.id}`, { method: "PATCH", body: { is_floor_plan: !t.is_floor_plan } }))}>
                        {t.is_floor_plan ? "Not a floor plan" : "Floor plan tag"}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {manage && (
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            run(async () => {
              await api("/admin/photo-tags", { method: "POST", body: { ...newTag(list), name: name.trim() } });
              setName("");
            });
          }}
        >
          <Label>
            New tag
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Terrace" />
          </Label>
          <Button type="submit" disabled={name.trim().length < 2}>
            Add
          </Button>
        </form>
      )}
    </Card>
  );
}
